/**
 * Application Designer — GRID layout geometry.
 *
 * Grid is the mode for pages whose elements have designer-chosen rectangles:
 * Appointment Booking, field rows, anything form-like and bounded. An item
 * occupies whole cells (`col, row, width, height`, 1-based) and never overlaps
 * a peer.
 *
 * There is no new geometry here. `lib/designerGrid.js` is already the whole
 * contract — overlap, containment, clamping, walk-toward-target move and
 * resize, first-available placement — with `bounds` as a parameter. This module
 * only binds it to an Application Designer page: it supplies the page's bounds,
 * it treats HIDDEN items as absent so a hidden element cannot block a visible
 * one, and it returns the original array when a change is refused so a caller
 * can treat identity as "nothing moved".
 *
 * The FLOW counterpart is `flowLayout.js`. The two modes share the editor shell
 * and the persistence format, not their geometry.
 */

import {
  clampItem,
  findFirstAvailablePlacement,
  tryMoveItem,
  tryResizeItem,
} from "@/lib/designerGrid";

export const DEFAULT_GRID_COLUMNS = 48;
export const DEFAULT_GRID_ROWS = 32;

export function gridBoundsOf({ columns, rows } = {}) {
  return {
    columns: Math.max(1, Number.parseInt(columns, 10) || DEFAULT_GRID_COLUMNS),
    rows: Math.max(1, Number.parseInt(rows, 10) || DEFAULT_GRID_ROWS),
  };
}

/** A hidden item is not on the page, so it is not a peer for overlap. */
function visiblePeers(items, excludeId) {
  return (items || []).filter((item) => item.visible && item.id !== excludeId);
}

export function visibleGridItems(items) {
  return (items || []).filter((item) => item.visible);
}

export function moveGridItem(items, id, col, row, bounds) {
  const target = (items || []).find((item) => item.id === id);
  if (!target || !target.visible) return items;
  const next = tryMoveItem(items, id, col, row, {
    bounds,
    peers: visiblePeers(items, id),
  });
  return next || items;
}

export function resizeGridItem(items, id, width, height, bounds) {
  const target = (items || []).find((item) => item.id === id);
  if (!target || !target.visible) return items;
  const next = tryResizeItem(items, id, width, height, {
    bounds,
    peers: visiblePeers(items, id),
  });
  return next || items;
}

export function hideGridItem(items, id) {
  return (items || []).map((item) => (item.id === id ? { ...item, visible: false } : item));
}

/**
 * Reveals a hidden item. Its stored rectangle is used when it is still free,
 * otherwise the item lands in the first rectangle that fits — showing an item
 * must never fail, and must never produce an overlap.
 */
export function showGridItem(items, id, bounds) {
  const target = (items || []).find((item) => item.id === id);
  if (!target || target.visible) return items;
  const peers = visiblePeers(items, id);
  const clamped = clampItem({ ...target, visible: true }, bounds);
  const blocked = peers.some(
    (peer) =>
      clamped.col < peer.col + peer.width &&
      clamped.col + clamped.width > peer.col &&
      clamped.row < peer.row + peer.height &&
      clamped.row + clamped.height > peer.row,
  );
  const placement = blocked
    ? findFirstAvailablePlacement(peers, bounds, clamped.width, clamped.height)
    : clamped;
  if (!placement) return items;
  return (items || []).map((item) =>
    item.id === id ? { ...clamped, ...placement, visible: true } : item,
  );
}

/** Repairs every visible item into a valid, non-overlapping rectangle. */
export function normalizeGridItems(items, bounds) {
  const placed = [];
  (items || []).forEach((item) => {
    if (!item.visible) {
      placed.push({ ...clampItem(item, bounds), visible: false });
      return;
    }
    const clamped = clampItem(item, bounds);
    const blocked = placed.some(
      (peer) =>
        peer.visible &&
        clamped.col < peer.col + peer.width &&
        clamped.col + clamped.width > peer.col &&
        clamped.row < peer.row + peer.height &&
        clamped.row + clamped.height > peer.row,
    );
    if (!blocked) {
      placed.push(clamped);
      return;
    }
    const placement = findFirstAvailablePlacement(
      placed.filter((peer) => peer.visible),
      bounds,
      clamped.width,
      clamped.height,
    );
    placed.push(placement ? { ...clamped, ...placement } : { ...clamped, visible: false });
  });
  return placed;
}
